import React, { useSyncExternalStore } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import {
  getSessionDegraded,
  subscribeSessionDegraded,
} from '../utils/appCheckRecovery';

/**
 * Persistent "your session went stale" bar.
 *
 * Nobody has been signed out when this shows — the short-lived tokens
 * behind the session couldn't be renewed — so the copy says "refresh",
 * not "expired" (which the team read as a login timeout). It is a last
 * resort: `sessionKeepAlive` renews the tokens when the app returns to
 * the foreground and reloads by itself after an idle lock-out.
 *
 * Replaces the old one-shot `window.confirm`, which could fire into an
 * unattended background tab and leave the user — once actually present —
 * with a silently broken app and no affordance. This bar stays up for as
 * long as the session is degraded and disappears on its own the moment
 * the shared subscriptions report recovery.
 *
 * Rendered inline at the top of <main> rather than fixed-positioned, so
 * there's no safe-area inset or MobileTabBar collision to reason about —
 * it pushes content down and scrolls with the page.
 */
const SessionDegradedBanner: React.FC = () => {
  const state = useSyncExternalStore(
    subscribeSessionDegraded,
    getSessionDegraded,
    getSessionDegraded,
  );

  if (!state.degraded) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4 border-b border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
    >
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="text-sm font-medium truncate">
          Connection needs a refresh — tap Reload.
        </span>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 dark:focus:ring-offset-dark-900"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Reload
      </button>
    </div>
  );
};

export default SessionDegradedBanner;
